import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DecimalField } from '@/components/form/fields/text/DecimalInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { CreateFinanceModelArgs, useFinanceModel, useUpdateFinanceModels } from '@/services/api/hooks/financemodels';
import { useRegions } from '@/services/api/hooks/region';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, FunctionComponent, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const EditFinanceModelPcm = z.object({
  value: z.string(),
  region: z.string()
});

const FormFields = z.object({
  name: z.string(),
  category: z.string(),
  manufacturer: z.string(),
  pcms: z.array(EditFinanceModelPcm)
});

type FormFields = z.infer<typeof FormFields>;

const FinanceModelForm: FunctionComponent<{
  defaultData?: FormFields;
  onSubmit: (data: FormFields) => Promise<void>;
}> = ({ defaultData, onSubmit }) => {
  const methods = useForm<FormFields>({
    resolver: zodResolver(FormFields),
    defaultValues: defaultData
  });

  return (
    <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
      <TextField id="name" label="Name" readonly />
      <TextField id="category" label="Category" readonly />
      <TextField id="manufacturer" label="Manufacturer" readonly />
      {defaultData?.pcms.map((pcm, index) => (
        <DecimalField key={index} id={`pcms.${index}.value`} label={`PCM - ${pcm.region}`} min={0} />
      ))}
      <div className="flex justify-end pt-2">
        <Button className="w-fit" variant={ButtonVariant.Primary} type="submit">
          Submit
        </Button>
      </div>
    </Form>
  );
};

const EditFinanceModelModal: FC<{ modelId: string; onClose: () => void }> = ({ modelId, onClose }) => {
  const [model] = useFinanceModel(modelId);
  const updateModels = useUpdateFinanceModels();
  const [regions] = useRegions();

  const onSubmit = async (data: FormFields) => {
    const mainDevicePayload: CreateFinanceModelArgs[] = [];

    data.pcms
      .filter(pcm => Number(pcm.value))
      .forEach(pcm => {
        const region = regions.find(region => region.code === pcm.region);

        if (!region) throw new Error('Missing region');

        const payload = {
          categoryId: model.categoryId,
          manufacturerId: model.manufacturerId,
          term: model.term,
          value: 120,
          type: model.financeModelType,
          regionId: region.id,
          pcmFactor: Number(pcm.value)
        };

        mainDevicePayload.push(payload);
      });

    await updateModels.mutateAsync(mainDevicePayload);
    onClose();
  };

  return (
    <FinanceModelForm
      defaultData={{
        name: model.name,
        pcms: model.pcms.map(pcm => {
          return {
            region: pcm.region,
            value: String(pcm.value)
          };
        }),
        category: model.category.name,
        manufacturer: model.manufacturer.name
      }}
      onSubmit={onSubmit}
    />
  );
};

const FinanceModelModal: FunctionComponent<ModalProps<{ modelId: string }>> = props => {
  const modelId = props.props?.modelId;

  const onClose = () => props.close();

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <Card className="w-full p-8">
          <Modal.Controls {...props}>
            <h1 className="text-2xl font-semibold">{modelId && 'Edit Finance Model'}</h1>
          </Modal.Controls>
          {modelId && <EditFinanceModelModal modelId={modelId} onClose={onClose} />}
        </Card>
      </Suspense>
    </Modal>
  );
};

export default FinanceModelModal;
